"use client";

import { FinanceEligibilityRequestCreateDTO } from "@alba-cars/common-modules";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { toast } from "sonner";

import PersonalInfoStep from "./PersonalInfoStep";
import QuestionStep1 from "./QuestionStep1";
import QuestionStep2 from "./QuestionStep2";
import { createStep1Schema, createStep2Schema, createStep4Schema, createFinanceFormSchema } from "../schema";
import MultistepForm, { StepData } from "@/components/common/forms/MultiStepForm";
import { useRouter } from "@/i18n/routing";
import { createFinanceEligibilityRequest } from "@/lib/api/finance-eligibility-request/create-finance-eligibility-request";
import { readUTMCookies } from "@/lib/utm-tracker";

export default function FinanceEligibilityForm() {
  const t = useTranslations("Finance.form");
  const tValidation = useTranslations("Finance.form.validation");
  const router = useRouter();

  const schemas = useMemo(
    () => ({
      0: createStep1Schema(tValidation),
      1: createStep2Schema(tValidation),
      2: createStep4Schema(tValidation),
    }),
    [tValidation],
  );

  const financeFormSchemaWithTranslations = useMemo(() => createFinanceFormSchema(tValidation), [tValidation]);

  const stepsData: StepData[] = [
    {
      component: QuestionStep1,
      onStepSubmit: async (data: any) => console.log("Step 1 Submitted", data),
      step: { label: t("steps.liabilities"), index: 0 },
      usesCustomNextButton: true,
    },
    {
      component: QuestionStep2,
      onStepSubmit: async (data: any) => console.log("Step 2 Submitted", data),
      step: { label: t("steps.salary"), index: 1 },
    },
    {
      component: PersonalInfoStep,
      onStepSubmit: async (data: any) => console.log("Step 3 Submitted", data),
      step: { label: t("steps.personalInfo"), index: 2 },
      submitStep: true,
    },
  ];

  const stepTitles = [t("steps.liabilities"), t("steps.salary"), t("steps.personalInfo")];

  const handleFinalSubmit = async (data: any) => {
    try {
      const parsedData = financeFormSchemaWithTranslations.parse(data);
      const marketingData = readUTMCookies();
      const dataWithMarketing = {
        ...parsedData,
        ...(Object.keys(marketingData).length > 0
          ? { marketing: { ...(parsedData.marketing || {}), ...marketingData } }
          : {}),
      };
      const plain = FinanceEligibilityRequestCreateDTO.fromPlain(dataWithMarketing);
      const response = await createFinanceEligibilityRequest(plain);

      const token = `fin_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem("financeToken", token);
      sessionStorage.setItem(
        "financeData",
        JSON.stringify({
          refId: (response?.data as any)?.refId,
          isEligible: response?.isEligible ?? false,
          ...parsedData,
        }),
      );
      router.push("/thank-you/finance?ref=success");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t("errors.submitFailed"));
      throw error;
    }
  };

  return (
    <MultistepForm
      stepsData={stepsData}
      onSubmit={handleFinalSubmit}
      schemas={schemas}
      TitleTag="h1"
      DescriptionTag="h2"
      stepTitles={stepTitles}
      title={t("title")}
      description={t("description")}
      showSubmitOnLastStep={true}
      buttonLabels={{
        back: t("buttons.back"),
        next: t("buttons.next"),
        submit: t("buttons.submit"),
        uploading: t("buttons.uploading"),
      }}
    />
  );
}
